import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DocumentHeaderFooterService } from '../document-header-footer.service';
import { DocumentFooter, FooterFormData } from '../document-header-footer.interface';

@Component({
    selector: 'app-add-edit-footer',
    templateUrl: './add-edit-footer.component.html',
    styleUrls: ['./add-edit-footer.component.scss']
})
export class AddEditFooterComponent implements OnInit {
    form!: FormGroup;
    isLoading = false;
    mode: 'add' | 'edit' = 'add';
    footer: DocumentFooter | null = null;
    previewImageUrl: string | null = null;
    paginationPreview: string = '';

    contentTypes = [
        { value: 'text', label: 'Text' },
        { value: 'image', label: 'Imagine' },
        { value: 'both', label: 'Text + Imagine' },
        { value: 'pagination', label: 'Doar Paginare' }
    ];

    alignments = [
        { value: 'left', label: 'Stânga' },
        { value: 'center', label: 'Centru' },
        { value: 'right', label: 'Dreapta' }
    ];

    paginationFormats = [
        { value: 'Pagina {page} din {total}', label: 'Pagina X din Y' },
        { value: 'Pag. {page}/{total}', label: 'Pag. X/Y' },
        { value: '{page}/{total}', label: 'X/Y' },
        { value: 'Pagina {page}', label: 'Pagina X (fără total)' }
    ];

    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<AddEditFooterComponent>,
        @Inject(MAT_DIALOG_DATA) public data: { mode: 'add' | 'edit'; footer?: DocumentFooter },
        private service: DocumentHeaderFooterService,
        private snackBar: MatSnackBar
    ) {
        this.mode = data.mode;
        this.footer = data.footer || null;
    }

    ngOnInit(): void {
        this.initForm();
        this.updatePaginationPreview();
    }

    initForm(): void {
        this.form = this.fb.group({
            name: [this.footer?.name || '', Validators.required],
            content_type: [this.footer?.content_type || 'text', Validators.required],
            text_content: [this.footer?.text_content || ''],
            image_path: [this.footer?.image_path || ''],
            alignment: [this.footer?.alignment || 'center', Validators.required],
            show_pagination: [this.footer?.show_pagination !== false],
            pagination_format: [this.footer?.pagination_format || 'Pagina {page} din {total}'],
            pagination_position: [this.footer?.pagination_position || 'right', Validators.required],
            is_active: [this.footer?.is_active !== false]
        });

        // Use image_url from backend for preview (full API URL)
        if (this.footer?.image_url) {
            this.previewImageUrl = this.footer.image_url;
        }

        // Listen for pagination format changes
        this.form.get('pagination_format')?.valueChanges.subscribe(() => {
            this.updatePaginationPreview();
        });
    }

    get showTextContent(): boolean {
        const type = this.form.get('content_type')?.value;
        return type === 'text' || type === 'both';
    }

    get showImageUpload(): boolean {
        const type = this.form.get('content_type')?.value;
        return type === 'image' || type === 'both';
    }

    get showPaginationOptions(): boolean {
        return this.form.get('show_pagination')?.value === true;
    }

    updatePaginationPreview(): void {
        const format = this.form.get('pagination_format')?.value || 'Pagina {page} din {total}';
        this.paginationPreview = format.replace('{page}', '1').replace('{total}', '5');
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const file = input.files[0];

            if (!file.type.startsWith('image/')) {
                this.snackBar.open('Vă rugăm selectați o imagine', 'Închide', { duration: 3000 });
                return;
            }

            if (file.size > 2 * 1024 * 1024) {
                this.snackBar.open('Imaginea trebuie să fie mai mică de 2MB', 'Închide', { duration: 3000 });
                return;
            }

            this.isLoading = true;
            this.service.uploadFooterImage(file).subscribe({
                next: (response: any) => {
                    this.form.patchValue({ image_path: response.data.path });
                    this.previewImageUrl = response.data.url;
                    this.isLoading = false;
                    this.snackBar.open('Imagine încărcată cu succes', 'Închide', { duration: 3000 });
                },
                error: (err) => {
                    console.error('Error uploading image:', err);
                    this.snackBar.open('Eroare la încărcarea imaginii', 'Închide', { duration: 3000 });
                    this.isLoading = false;
                }
            });
        }
    }

    removeImage(): void {
        this.form.patchValue({ image_path: '' });
        this.previewImageUrl = null;
    }

    onSubmit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        const payload: FooterFormData = {
            ...this.form.value
        };

        if (this.mode === 'edit' && this.footer) {
            payload.id = this.footer.id;
        }

        this.isLoading = true;

        const request = this.mode === 'add'
            ? this.service.storeFooter(payload)
            : this.service.updateFooter(payload);

        request.subscribe({
            next: (response: any) => {
                this.snackBar.open(
                    this.mode === 'add' ? 'Subsol creat cu succes' : 'Subsol actualizat cu succes',
                    'Închide',
                    { duration: 3000 }
                );
                this.dialogRef.close(true);
            },
            error: (err) => {
                const message = err.error?.message || 'Eroare la salvarea subsolului';
                this.snackBar.open(message, 'Închide', { duration: 3000 });
                this.isLoading = false;
            }
        });
    }

    onCancel(): void {
        this.dialogRef.close(false);
    }
}
